export function chunkArray<T>(items: readonly T[], size: number): T[][] {
  const chunks: T[][] = [];
  for (let index = 0; index < items.length; index += size) {
    chunks.push(items.slice(index, index + size));
  }
  return chunks;
}

export const detectArrayChanges = <T>(
  prevArray: T[] | null | undefined,
  currArray: T[]
): {
  newItems: T[];
  removedItems: T[];
  unChangedItems: T[];
} => {
  if (prevArray == null) {
    prevArray = [];
  }

  const prevSet = new Set(prevArray);
  const currSet = new Set(currArray);

  const newItems = currArray.filter((item) => !prevSet.has(item));
  const removedItems = prevArray.filter((item) => !currSet.has(item));
  const unChangedItems = currArray.filter((item) => prevSet.has(item));

  return { newItems, removedItems, unChangedItems };
};

export const detectObjectArrayChanges = <T>(
  prevArray: T[] | null | undefined,
  currArray: T[],
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  prop: keyof T | ((item: T) => any)
): {
  newItems: T[];
  removedItems: T[];
  unChangedItems: T[];
} => {
  if (prevArray == null) {
    prevArray = [];
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const getValue = (item: T, prop: keyof T | ((item: T) => any)): any => {
    if (typeof prop === 'function') {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-return
      return prop(item);
    }
    const value = item[prop];
    if (typeof value === 'function') {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-return
      return value.call(item);
    }
    return value;
  };

  // Create Maps for O(1) lookup performance
  const previousItemsMap = new Map<string, T>();
  const currentItemsMap = new Map<string, T>();

  // Populate Maps - O(n) complexity
  for (const item of prevArray) {
    const key = String(getValue(item, prop));
    previousItemsMap.set(key, item);
  }
  for (const item of currArray) {
    const key = String(getValue(item, prop));
    currentItemsMap.set(key, item);
  }

  // Find new items (in current but not in previous) - O(n) complexity
  const newItems: T[] = [];
  for (const item of currArray) {
    const key = String(getValue(item, prop));
    if (!previousItemsMap.has(key)) {
      newItems.push(item);
    }
  }

  // Find removed items (in previous but not in current) - O(n) complexity
  const removedItems: T[] = [];
  for (const item of prevArray) {
    const key = String(getValue(item, prop));
    if (!currentItemsMap.has(key)) {
      removedItems.push(item);
    }
  }

  // Find unchanged items (in both arrays) - O(n) complexity
  const unChangedItems: T[] = [];
  for (const item of prevArray) {
    const key = String(getValue(item, prop));
    if (currentItemsMap.has(key)) {
      unChangedItems.push(item);
    }
  }

  return { newItems, removedItems, unChangedItems };
};
